import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import withRouter from "Components/Common/withRouter";
import Breadcrumb from "Components/organisms/breadcrumb";
import ActionButton from "Components/atoms/buttons/action-button";
import { HelpIcon } from "Components/atoms/icons";
import useManual from "hooks/useManual";
import ApproveButton from "Components/atoms/buttons/approve-button";
import RejectButton from "Components/atoms/buttons/reject-button";
import LoadingState from "Components/organisms/loading-state";
import Chip from "Components/atoms/chip";
import ExternalLink from "Components/atoms/external-link";
import Modal from "Components/molecule/modal";
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons";
import TextAreaInput from "Components/molecule/inputs/textarea-inpuit";
import ContinuingEducationRequest from "models/continuing-education";
import { CONTINUING_EDUCATION_STATUS, CONTINUING_EDUCATION_STATUS_VARIANT } from "models/continuing-education-status";
import { SCREENS } from "models/screen";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import {
    approveContinuingEducationRequest,
    declineContinuingEducationRequest,
    getContinuingEducationRequest,
} from "slices/forms/continuing-education/thunk";
import { ContinuingEducationApprovalStage, canEdit, getApprovalStage, getStageApproverId, isDeclined } from "utils/continuing-education";
import { navigateToContinuingEducationList, navigateToEditContinuingEducation, navigateBackOrHome, openContinuingEducationPrint } from "utils/navigator";
import { formatCurrency } from "utils/tools";

const selectProfile = createSelector(
    (state: any) => state.Profile,
    ({ employee }: ProfileSliceState) => ({ employee })
);

const selectPermissions = createSelector(
    (state: any) => state.Permissions,
    ({ screens }: PermissionsSliceState) => ({ screens })
);

const ContinuingEducationDetailsPage = (props: any) => {
    document.title = "Continuing Education Request | IbexIQ";
    const { handleOpenManual } = useManual("forms/continuing-education");
    const id = props.router.params.id as string;
    const { employee } = useSelector(selectProfile);
    const { screens } = useSelector(selectPermissions);

    const [request, setRequest] = useState<ContinuingEducationRequest | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [showApproveModal, setShowApproveModal] = useState<boolean>(false);
    const [showDeclineModal, setShowDeclineModal] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [comment, setComment] = useState<string>('');
    const [commentError, setCommentError] = useState<boolean>(false);

    const fetchRequest = async () => {
        const res = await getContinuingEducationRequest(id);
        if (res) setRequest(res);
        return res;
    }

    const initializePage = async () => {
        setIsLoading(true)
        const res = await fetchRequest();

        const isAdmin = screens.some(s => [SCREENS.ADMIN, SCREENS.HR_ADMIN, SCREENS.FIN_ADMIN].includes(s.code as SCREENS));
        const isParty = res && employee && [res.employeeId, res.managerApproverId, res.secondaryApproverId].includes(employee.id);

        if (res && !isAdmin && !isParty) {
            navigateBackOrHome(props.router.navigate)
            return;
        }

        setIsLoading(false)
    }

    useEffect(() => {
        initializePage()
    }, [])

    const handleBack = () => navigateToContinuingEducationList(props.router.navigate)
    const handleEdit = () => navigateToEditContinuingEducation(props.router.navigate, id)

    const stage = useMemo(() => request ? getApprovalStage(request) : null, [request]);
    const canAct = !!request && !!employee && stage !== null
        && stage !== ContinuingEducationApprovalStage.APPROVED
        && getStageApproverId(request, stage) === employee.id;

    const handleShowApproveModal = () => {
        setCommentError(false)
        setShowApproveModal(true)
    }
    const handleHideApproveModal = () => setShowApproveModal(false)

    const handleConfirmApprove = async () => {
        setIsSubmitting(true)
        const res = await approveContinuingEducationRequest(id, comment || null);
        if (res) {
            setRequest(res)
            handleHideApproveModal()
        }
        setIsSubmitting(false)
    }

    const handleDeclineClick = () => {
        if (!comment.trim()) {
            setCommentError(true)
            return;
        }
        setShowDeclineModal(true)
    }
    const handleHideDeclineModal = () => setShowDeclineModal(false)

    const handleConfirmDecline = async () => {
        setIsSubmitting(true)
        const res = await declineContinuingEducationRequest(id, comment);
        if (res) {
            setRequest(res)
            handleHideDeclineModal()
        }
        setIsSubmitting(false)
    }

    const employeeName = (e?: { commonName?: string | null, firstName?: string, lastName?: string } | null) =>
        e ? (e.commonName ?? e.firstName) + " " + e.lastName : "Not found"

    const handlePrint = () => openContinuingEducationPrint(id)

    return <div className="page-content">
        <div className="container-fluid" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            <Breadcrumb
                title={"Forms"}
                breadcrumbItem={"Continuing Education Request"}
                actions={<>
                    <ActionButton onClick={handleBack}>Back</ActionButton>
                    {request?.status?.description === CONTINUING_EDUCATION_STATUS.APPROVED && <>
                        <ActionButton onClick={handlePrint}>Print / Save as PDF</ActionButton>
                    </>}
                    {request && canEdit(request, employee?.id) && (
                        <ActionButton onClick={handleEdit}>{isDeclined(request) ? "Edit & Resubmit" : "Edit"}</ActionButton>
                    )}
                    <ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>
                </>}
            />

            {isLoading || !request ? (
                <LoadingState
                    title="Loading Request"
                    subtitle="Fetching request details…"
                />
            ) : (
                <>
                    <div className="section-container">
                        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                            <div>
                                <h4 className="mb-1">{request.courseName}</h4>
                                <Chip variant={CONTINUING_EDUCATION_STATUS_VARIANT[request.status?.description ?? '']}>
                                    {request.status?.description ?? "Not found"}
                                </Chip>
                            </div>
                            {canAct && (
                                <div className="d-flex gap-2">
                                    <ApproveButton onClick={handleShowApproveModal}>Approve</ApproveButton>
                                    <RejectButton disabled={isSubmitting} onClick={handleDeclineClick}>Decline</RejectButton>
                                </div>
                            )}
                        </div>
                        <div className="row g-3">
                            {[
                                { label: "Employee", value: employeeName(request.employee) },
                                { label: "Type", value: request.type?.description ?? "-" },
                                { label: "Topic", value: request.topic?.description ?? "-" },
                                { label: "Environment", value: request.environment?.description ?? "-" },
                                { label: "Manager", value: employeeName(request.managerApprover) },
                                { label: "Secondary Approver", value: request.secondaryApproverId ? employeeName(request.secondaryApprover) : "Not needed" },
                                { label: "Cost (CAD)", value: formatCurrency(request.cost) },
                                { label: "Remaining Budget (CAD)", value: formatCurrency(request.remainingBudget) },
                                { label: "Course Hours", value: request.courseHours },
                                { label: "Course Start Date", value: request.courseStartDate?.toString().split("T")[0] },
                                { label: "Course End Date", value: request.courseEndDate?.toString().split("T")[0] },
                            ].map(({ label, value }) => (
                                <div className="col-6 col-md-3" key={label}>
                                    <div className="text-muted small">{label}</div>
                                    <div>{value}</div>
                                </div>
                            ))}
                            {request.courseLink && (
                                <div className="col-12">
                                    <div className="text-muted small">Course Link</div>
                                    <ExternalLink href={request.courseLink}>
                                        {request.courseLink}
                                    </ExternalLink>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="section-container">
                        <h4>Course Description</h4>
                        <p className="mb-0">{request.courseDescription}</p>
                    </div>

                    {(request.managerComment || request.secondaryComment) && (
                        <div className="section-container">
                            <h4>Comments</h4>
                            <div className="row g-3">
                                {[
                                    ...(request.managerComment ? [{ label: "Manager", value: request.managerComment }] : []),
                                    ...(request.secondaryComment ? [{ label: "Secondary Approver", value: request.secondaryComment }] : []),
                                ].map(({ label, value }) => (
                                    <div className="col-12 col-md-6" key={label}>
                                        <div className="text-muted small">{label}</div>
                                        <div>{value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {canAct && (
                        <div className="section-container">
                            <TextAreaInput
                                name="comment"
                                label="Comment (required to decline)"
                                placeholder="Add a comment"
                                value={comment}
                                onChange={(e) => {
                                    setComment(e.target.value)
                                    setCommentError(false)
                                }}
                                error={commentError}
                                message={commentError ? "Comment is required to decline." : undefined}
                                className="col-12 text-area-input"
                                max={1000}
                            />
                        </div>
                    )}
                </>
            )}

            <Modal
                isOpen={showApproveModal}
                onClose={handleHideApproveModal}
                title={"Confirm"}
                subtitle={"Are you sure you want to approve this request?"}
            >
                <AreYouSureButtons
                    disable={isSubmitting}
                    acceptButtonStyle={{ width: '150px' }}
                    declineButtonStyle={{ width: '150px' }}
                    handleAccept={handleConfirmApprove}
                    handleDecline={handleHideApproveModal}
                    acceptText={isSubmitting ? "Approving..." : "Approve"}
                    declineText={"Cancel"}
                />
            </Modal>

            <Modal
                isOpen={showDeclineModal}
                onClose={handleHideDeclineModal}
                title={"Confirm"}
                subtitle={"Are you sure you want to decline this request?"}
            >
                <AreYouSureButtons
                    disable={isSubmitting}
                    acceptButtonStyle={{ width: '150px' }}
                    declineButtonStyle={{ width: '150px' }}
                    handleAccept={handleConfirmDecline}
                    handleDecline={handleHideDeclineModal}
                    acceptText={isSubmitting ? "Declining..." : "Decline"}
                    declineText={"Cancel"}
                />
            </Modal>
        </div>
    </div>
}

export default withRouter(ContinuingEducationDetailsPage);
